import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { HANDLE_404 } from '../core/constants/context-tokens';

@Injectable({
  providedIn: 'root',
})
export class AvatarService {
  constructor(
    private readonly _http: HttpClient,
    private readonly _msalService: MsalService,
    private readonly _msalBroadcastService: MsalBroadcastService
  ) {}

  /** Loads the signed-in user's own avatar directly from Graph (/me/photo), same as before the backend proxy was introduced. */
  async getMyAvatar(): Promise<string | null> {
    await firstValueFrom(
      this._msalBroadcastService.inProgress$.pipe(filter((status) => status === InteractionStatus.None))
    );

    const tokenResponse = await firstValueFrom(
      this._msalService.acquireTokenSilent({ scopes: environment.apiADConfig.scopes })
    );

    if (!tokenResponse.accessToken) {
      return null;
    }

    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status !== 404) {
          console.error(`Failed to load own avatar from Graph API: HTTP ${response.status}`);
        }
        return null;
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to load own avatar from Graph API:', error);
      return null;
    }
  }

  async getEntraIDAvatar(email?: string): Promise<string | null> {
    if (!email) return null;

    try {
      const blob = await firstValueFrom(
        this._http.get(`${environment.api}/api/Users/photo`, {
          params: { email },
          responseType: 'blob',
          context: new HttpContext().set(HANDLE_404, true),
        })
      );
      // the backend returns 204 No Content (an empty blob) when the user has no photo set
      if (!blob || blob.size === 0) {
        return null;
      }
      return URL.createObjectURL(blob);
    } catch (error: any) {
      // a 404 here would be unexpected now (the backend returns 204 for "no photo"), but is still
      // excluded from the global error interceptor's logging via HANDLE_404 just in case
      if (error?.status !== 404) {
        console.error(`Failed to load avatar for ${email}:`, error);
      }
      return null;
    }
  }
}
